/**
 * Contexte d'authentification React.
 * Fournit: user, login, logout, isAdmin, isEncadrant, isAdherent, isSecretariat, isTresorier, hasRole.
 * Gere le stockage du token JWT dans localStorage et le refresh automatique via /auth/me.
 */
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { signalerSiIndisponible } from '../components/RedemarrageServeur';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// 08/09/2026 (demande club "positionner un selecteur de role en admin afin de presenter les
// differentes vues lors de presentation") : liste des roles simulables et leur libelle, utilisee
// par le selecteur (Layout.jsx) et par toute autre UI qui a besoin d'afficher les options.
export const PREVIEWABLE_ROLES = [
  { value: 'admin', label: 'Administrateur' },
  { value: 'secretariat', label: 'Secretariat' },
  { value: 'tresorier', label: 'Tresorier' },
  { value: 'gestionnaire_materiel', label: 'Gestionnaire materiel' },
  { value: 'encadrant', label: 'Encadrant' },
  { value: 'responsable_technique', label: 'Responsable technique' },
  { value: 'organisateur', label: 'Organisateur' },
  { value: 'adherent', label: 'Adherent' },
];
const PREVIEW_ROLE_STORAGE_KEY = 'cesam_preview_role';

// 09/09/2026 (demande club "n'affiche le role adherent qu'avec le jeu de donnee de test, tu peux
// connecter directement a sophie bernard") : contrairement aux autres roles previsualises (purs
// cosmetiques - meme compte admin, juste un role affiche different), "Adherent" simule un VRAI
// adherent de demonstration - sinon les pages "Mon profil"/"Mon materiel" affichent les donnees
// (ou l'absence de donnees) du compte admin lui-meme, peu representatif. Aucune usurpation cote
// backend : le JWT reste celui de l'admin, qui a deja legitimement le droit de consulter N'IMPORTE
// QUEL plongeur via les routes admin (GET /divers/{id}...) - seul le CHOIX de quel plongeur
// consulter change, pas les permissions. Reserve au jeu de donnees de test (is_test_data=1) :
// n'a de sens qu'avec lui, jamais mele a de vraies donnees d'adherents.
export const SIMULATED_ADHERENT_DIVER_ID = 211; // Sophie Bernard (sophie.bernard@email.fr)

// 08/09/2026 (demande club "un slider dans le meme genre que le changement de role pour
// visualiser les donnees en test") : contrairement au preview de role (purement cosmetique,
// aucun appel backend), previsualiser les donnees de test change reellement ce que renvoie
// l'API (app_mode filtre les requetes SQL cote serveur, cf. get_app_mode()/middleware dans
// server_mysql.py) - ce header est ce qui transporte l'apercu jusqu'au backend, qui ne l'honore
// que si le porteur du token est reellement admin (jamais fait confiance au frontend seul).
const PREVIEW_APP_MODE_HEADER = 'X-Preview-App-Mode';
const PREVIEW_APP_MODE_STORAGE_KEY = 'cesam_preview_app_mode';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** Jeton JWT expire (ou a moins de 5 s de l'etre), lu dans sa charge utile sans appel serveur.
 *  Un jeton illisible n'est pas juge expire : le serveur reste seul juge dans ce cas. */
const jetonExpire = (jeton) => {
  try {
    const charge = JSON.parse(atob(jeton.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof charge.exp === 'number' && charge.exp * 1000 < Date.now() + 5000;
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // sessionStorage (pas localStorage) : le mode demo ne doit pas survivre a une fermeture
  // d'onglet/navigateur - on ne veut pas qu'un admin rouvre le site un autre jour et se retrouve
  // silencieusement bride par un aperçu de role oublie la fois precedente.
  const [previewRole, setPreviewRoleState] = useState(() => sessionStorage.getItem(PREVIEW_ROLE_STORAGE_KEY) || null);
  const [previewAppMode, setPreviewAppModeState] = useState(() => sessionStorage.getItem(PREVIEW_APP_MODE_STORAGE_KEY) || null);

  // 24/08/2026 (constat d'audit "axios-instance-not-memoized", 19/08) : axios.create() sans
  // useMemo cree une nouvelle reference a chaque re-render du provider - des pages comme
  // CursusPage/ReservationsPage mettent axiosInstance en dependance de leur useCallback/
  // useEffect, donc tout re-render du provider invalidait leur cache et redeclenchait des
  // fetchs inutiles.
  const axiosInstance = useMemo(() => axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }), [token]);

  useEffect(() => {
    // 1.63.5.0 : jeton deja expire au chargement -> deconnexion sans appel (voir jetonExpire).
    if (token && jetonExpire(token)) {
      logout();
      setLoading(false);
    } else if (token) {
      axiosInstance.defaults.headers.Authorization = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      const data = response.data;
      // Normalize roles: support both old "role" and new "roles" format
      if (!data.roles) {
        data.roles = data.role ? [data.role] : ['adherent'];
      }
      setUser(data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    // Normalize roles
    if (!userData.roles) {
      userData.roles = userData.role ? [userData.role] : ['adherent'];
    }
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const loginWithToken = (newToken, userData) => {
    if (!userData.roles) {
      userData.roles = userData.role ? [userData.role] : ['adherent'];
    }
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    // 08/09/2026 : un aperçu de role/de donnees test ne doit jamais survivre a une deconnexion -
    // le prochain compte a se connecter sur ce navigateur (autre membre du bureau sur le meme
    // poste, par exemple) ne doit pas heriter silencieusement d'un apercu de la session precedente.
    // 1.59.7.0 : sans rechargement ici - la deconnexion mene deja a la page de connexion.
    clearPreviewRole(false);
    clearPreviewAppMode(false);
  };

  // 08/09/2026 (demande club "positionner un selecteur de role en admin afin de presenter les
  // differentes vues lors de presentation") : mode "apercu" reserve aux vrais admins, qui fait
  // voir a l'interface un role different SANS toucher au token JWT ni a axiosInstance - les
  // appels API continuent de s'authentifier avec les vrais droits admin en arriere-plan (aucune
  // perte/gain reel de permission, uniquement un mensonge assume cote affichage). realIsAdmin
  // se base sur `user` (le vrai compte), jamais sur `effectiveUser`, sinon le selecteur/bandeau de
  // sortie disparaitrait des qu'on previsualise un role different d'admin - il faut au contraire
  // pouvoir en sortir a tout moment.
  const realIsAdmin = user?.roles?.includes('admin') || false;
  // 1.58.4.0 (demande du club) : apercu de role, donnees de test et bandeau du mode test reserves au
  // compte webmaster (indicateur `outils_test` calcule par le serveur dans /auth/me).
  const outilsTest = realIsAdmin && !!user?.outils_test;

  // 1.59.7.0 (demande du club : "forcer un refresh de la page lors de la bascule en mode test ou
  // d'un changement de role") : chaque page charge ses donnees une fois ; sans rechargement, la
  // page en cours gardait celles d'avant la bascule. L'apercu est deja en sessionStorage quand on
  // recharge, il survit donc au rechargement. Un seul rechargement meme si deux bascules
  // s'enchainent (fin des donnees de test qui retire aussi l'apercu "Adherent").
  let rechargementPrevu = false;
  const recharger = () => {
    if (rechargementPrevu) return;
    rechargementPrevu = true;
    setTimeout(() => window.location.reload(), 30);
  };

  const setPreviewRole = (role) => {
    if (!outilsTest) return;
    if (!role || role === 'admin') {
      clearPreviewRole();
      return;
    }
    sessionStorage.setItem(PREVIEW_ROLE_STORAGE_KEY, role);
    setPreviewRoleState(role);
    recharger();
  };

  const clearPreviewRole = (rechargerPage = true) => {
    sessionStorage.removeItem(PREVIEW_ROLE_STORAGE_KEY);
    setPreviewRoleState(null);
    if (rechargerPage !== false) recharger();
  };

  // 08/09/2026 (demande club "un slider dans le meme genre que le changement de role pour
  // visualiser les donnees en test") : contrairement au preview de role, ceci change reellement
  // les donnees renvoyees par l'API (envoie le header X-Preview-App-Mode, honore cote backend
  // seulement si le vrai compte est admin - cf. middleware server_mysql.py). 'production' n'a pas
  // besoin d'etre "previsualise" (c'est deja le mode par defaut pour tout le monde), donc setter
  // ne retient que 'test'.
  const setPreviewAppMode = (mode) => {
    if (!outilsTest) return;
    if (mode !== 'test') {
      clearPreviewAppMode();
      return;
    }
    sessionStorage.setItem(PREVIEW_APP_MODE_STORAGE_KEY, mode);
    setPreviewAppModeState(mode);
    recharger();
  };

  const clearPreviewAppMode = (rechargerPage = true) => {
    sessionStorage.removeItem(PREVIEW_APP_MODE_STORAGE_KEY);
    setPreviewAppModeState(null);
    // 09/09/2026 : le role "Adherent" n'a de sens qu'avec le jeu de donnees de test (cf.
    // previewAdherentDiverId plus haut) - sans cette garde, desactiver le mode test alors que ce
    // role est actif laisserait l'apercu "Adherent" tourner sans plus aucune donnee simulee
    // derriere (navigation restreinte a un adherent, mais plus de fiche a afficher).
    if (previewRole === 'adherent') {
      clearPreviewRole(rechargerPage);
    }
    if (rechargerPage !== false) recharger();
  };

  // Vue "effective" utilisee par tout le reste de l'app (menu, ProtectedRoute, pages) - identique
  // au vrai user tant qu'aucun apercu n'est actif, sinon un simple clone avec `roles` remplace par
  // le role simule (garde defensive supplementaire sur realIsAdmin : meme si previewRole restait
  // en sessionStorage d'une session precedente, il ne s'applique jamais a un compte non-admin).
  const effectiveUser = useMemo(() => {
    if (previewRole && outilsTest) {
      return { ...user, roles: [previewRole] };
    }
    return user;
  }, [user, previewRole, outilsTest]);

  // 08/09/2026 (apercu donnees test) : intercepteur de REQUETE plutot que header fixe sur
  // axiosInstance.defaults - axiosInstance n'est recree (useMemo) que si `token` change, donc un
  // header fige au moment de la creation resterait perime des que previewAppMode change ensuite.
  // Reenregistre a chaque changement de previewAppMode/realIsAdmin, l'ancien intercepteur est
  // ejecte au nettoyage pour ne pas en empiler plusieurs au fil des re-renders.
  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.request.use((config) => {
      if (previewAppMode && outilsTest) {
        config.headers[PREVIEW_APP_MODE_HEADER] = previewAppMode;
      }
      return config;
    });
    return () => axiosInstance.interceptors.request.eject(interceptorId);
  }, [axiosInstance, previewAppMode, outilsTest]);

  // 1.63.5.0 (incident du 14/10/2026 : "je n'ai plus acces a aucun service") : en rouvrant
  // l'application avec un jeton expire, plusieurs requetes partaient EN MEME TEMPS (compteurs du
  // menu, /auth/me...) et revenaient toutes en 401 avant que la deconnexion ci-dessous n'ait lieu.
  // fail2ban (SWAG, regle nginx-unauthorized : 5 reponses 401) bannissait alors l'adresse du
  // telephone pour TOUS les services de la machine, pas seulement l'espace adherent - deja arrive
  // a un adherent le 13/10. L'expiration se lit dans le jeton lui-meme : un jeton expire n'est plus
  // envoye du tout, la deconnexion se fait sans aucun appel au serveur.
  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.request.use((config) => {
      // Le jeton lu est celui que la requete va EMPORTER (en-tete), pas celui du stockage : les
      // requetes parties dans la meme seconde gardent l'ancien en-tete apres la deconnexion.
      const entete = String(config.headers?.Authorization || config.headers?.get?.('Authorization') || '');
      const jeton = entete.startsWith('Bearer ') ? entete.slice(7) : '';
      if (jeton && jetonExpire(jeton)) {
        if (localStorage.getItem('token')) {
          logout();
          toast({ title: 'Session expirée', description: 'Veuillez vous reconnecter.', variant: 'destructive' });
        }
        return Promise.reject(new axios.Cancel('Session expirée'));
      }
      return config;
    });
    return () => axiosInstance.interceptors.request.eject(interceptorId);
  }, [axiosInstance]);

  // 04/09/2026 (signalement club "le backoffice semble down") : le token JWT expire au bout de
  // 24h (voir backend), mais rien ne le detectait pendant la session - chaque appel API qui
  // recevait un 401 apres expiration echouait silencieusement (souvent avale par un .catch(() =>
  // {}) sur les pastilles de notification en arriere-plan), laissant l'utilisateur bloque sur une
  // page qui ne repond plus, sans le moindre message ni redirection vers la connexion. Un
  // intercepteur de reponse global sur axiosInstance detecte desormais tout 401 (expire ou
  // invalide) et deconnecte proprement - ProtectedRoute (App.js) redirige alors automatiquement
  // vers /login des que `user` redevient null, sans code de redirection supplementaire ici.
  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // 1.51.0.0 : serveur en cours de redemarrage -> ecran "mise a jour en cours".
        signalerSiIndisponible(error);
        if (error?.response?.status === 401) {
          const wasLoggedIn = !!localStorage.getItem('token');
          logout();
          if (wasLoggedIn) {
            toast({ title: 'Session expiree', description: 'Veuillez vous reconnecter.', variant: 'destructive' });
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axiosInstance.interceptors.response.eject(interceptorId);
  }, [axiosInstance]);

  const hasRole = (role) => effectiveUser?.roles?.includes(role) || false;
  const hasAnyRole = (...roles) => roles.some(r => effectiveUser?.roles?.includes(r));

  const isAdmin = () => hasRole('admin');
  const isEncadrant = () => hasAnyRole('encadrant', 'admin');
  const isAdherent = () => hasRole('adherent');
  const isGestionnaireMateriel = () => hasAnyRole('gestionnaire_materiel', 'admin');
  const isSecretariat = () => hasAnyRole('secretariat', 'admin');
  const isTresorier = () => hasAnyRole('tresorier', 'admin');
  // 11/09/2026 (demande club) : droit de definir les cursus de formation. L'admin l'a par
  // heritage, comme pour les autres roles ci-dessus.
  const isResponsableTechnique = () => hasAnyRole('responsable_technique', 'admin');

  const value = {
    // `user` expose la vue effective (simulee si un apercu de role est actif) - c'est ce que
    // consultent toutes les pages/le menu/ProtectedRoute pour un apercu coherent partout.
    // `realUser`/`realIsAdmin` : le vrai compte connecte, pour le selecteur/bandeau de sortie qui
    // doivent rester utilisables meme quand on previsualise un role non-admin.
    user: effectiveUser,
    realUser: user,
    realIsAdmin,
    outilsTest,
    previewRole,
    isPreviewingRole: !!(previewRole && outilsTest),
    setPreviewRole,
    clearPreviewRole,
    previewAppMode,
    isPreviewingAppMode: !!(previewAppMode && outilsTest),
    setPreviewAppMode,
    clearPreviewAppMode,
    // n'a de sens qu'avec le jeu de donnees de test actif - exige les deux conditions, pas
    // seulement previewRole (qui pourrait rester positionne en sessionStorage alors que le mode
    // test a ete desactive depuis).
    previewAdherentDiverId: (previewRole === 'adherent' && outilsTest && !!previewAppMode) ? SIMULATED_ADHERENT_DIVER_ID : null,
    token,
    loading,
    login,
    loginWithToken,
    logout,
    isAdmin,
    isEncadrant,
    isAdherent,
    isGestionnaireMateriel,
    isSecretariat,
    isTresorier,
    isResponsableTechnique,
    hasRole,
    hasAnyRole,
    axiosInstance
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
